import React from 'react';
import { Info, CheckCircle2, AlertTriangle, XCircle, X } from 'lucide-react';
import { cn } from './cn';

/**
 * InfoBanner — inline, lightweight contextual message (info/success/warning/
 * danger) with an auto icon and optional dismiss. Lower visual weight than
 * AttentionStrip.
 *
 * Props:
 *  - variant:   'info' (default) | 'success' | 'warning' | 'danger'
 *  - title:     optional bold line
 *  - children:  message body
 *  - onDismiss: optional; shows a close button
 *  - icon:      optional lucide icon override (else picked from variant)
 */
const VARIANTS = {
  info:    { wrap: 'border-ga-info/30 bg-ga-info/10 text-ga-text-primary',       icon: 'text-ga-info',    Icon: Info },
  success: { wrap: 'border-ga-success/30 bg-ga-success/10 text-ga-text-primary', icon: 'text-ga-success', Icon: CheckCircle2 },
  warning: { wrap: 'border-ga-warning/30 bg-ga-warning/10 text-ga-text-primary', icon: 'text-ga-warning', Icon: AlertTriangle },
  danger:  { wrap: 'border-ga-danger/30 bg-ga-danger/10 text-ga-text-primary',   icon: 'text-ga-danger',  Icon: XCircle },
};

export default function InfoBanner({ variant = 'info', title, children, onDismiss, icon, className, ...props }) {
  const cfg = VARIANTS[variant] || VARIANTS.info;
  const Icon = icon || cfg.Icon;
  return (
    <div
      role="status"
      className={cn('flex items-start gap-3 rounded-ga-md border px-4 py-3 text-sm', cfg.wrap, className)}
      {...props}
    >
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', cfg.icon)} aria-hidden />
      <div className="min-w-0 flex-1">
        {title && <div className="font-semibold text-ga-text-primary">{title}</div>}
        {children && <div className={cn('text-[13px] text-ga-text-muted', title && 'mt-0.5')}>{children}</div>}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="shrink-0 rounded-ga-sm p-0.5 text-ga-text-subtle transition-colors hover:text-ga-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ga-focus"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      )}
    </div>
  );
}
